import { differenceInDays, isAfter, isBefore } from 'date-fns';
import { types, flow, getEnv, getParent } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';
import { generateFee } from '@ourbranch/fee-utils';
import { advancedConnectedHomeStates, leaseLoanStates } from '@ourbranch/lookups';
import { toJS } from 'mobx';
import cloneDeep from 'lodash-es/cloneDeep';

import { getMerged } from 'customer/components/policy/merge-segments';
import { types as GQLTypes } from 'core/helpers/sanitize';
import { isPolicyOverdue, clean } from '@ourbranch/policy-utils';
import {
  REMOVE_HOLD_CARD,
  ADD_HOLD_CARD,
  GET_HOLD_CARDS,
  PREVIEW_POLICY_CHANGE_SCRUB_INCIDENTS,
  GET_RENEWAL_POLICY_ID,
  GET_RENEWAL_PRICE_COMPARISON_DETAILS,
  CHANGE_POLICY,
  CHANGE_SEGMENT,
  ADD_SEGMENT,
  CONFIRM,
  SUBMIT_CHANGES_FOR_APPROVAL,
  SEND_APPROVED_REQUESTED_CHANGES_COMMUNICATION,
  RECREATE_APPLICATION,
  ORDER_INSPECTION,
  GET_INSPECTION_STATUS,
  GET_CD_DIFF,
  GET_BIX_CONVERSION_SIGNATURE_FROM_TABLE,
  GET_PALOMAR_POLICY_DOC,
  REWRITE_POLICY,
  GET_POLICY_PAID_AND_PENDING,
  GET_POLICY_AUTO_PREMIUM_BREAKDOWN_PDF,
  SUBMIT_FARM_POLICY_FOR_UNDERWRITING_APPROVAL
} from 'customer/components/policy/policy.queries';
import {
  RUN_MANUAL_CHARGE,
  MODIFY_CREDIT_CARD,
  ADD_CREDIT_CARD,
  REMOVE_PAYMENT_METHOD
} from 'customer/components/policy/payment-tab/payment.queries';
import { track } from 'core/helpers/analytics';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { currencyFormatter, awsDateToDateFormatter } from 'core/helpers/formatters';
import Fee from './models/fee';
import PolicyEquityStatus from './models/policy-equity-status';
import BillingDetails from './models/billing-details';

export const PolicyTabs = {
  SETTINGS: 'SETTINGS',
  PAYMENT: 'PAYMENT',
  DETAILS: 'DETAILS',
  DOCUMENTS: 'DOCUMENTS',
  INCIDENTS: 'INCIDENTS'
};

export const PolicyModalTypes = {
  PREVIEW: 'PREVIEW',
  RENEWAL: 'RENEWAL',
  SUBMITTED_FOR_APPROVAL: 'SUBMITTED_FOR_APPROVAL',
  PREVIEW_LINK_EXPIRED: 'PREVIEW_LINK_EXPIRED',
  WARNING: 'WARNING',
  FARM_UW_QUESTIONS: 'FARM_UW_QUESTIONS'
};

export const PolicyStore = types
  .model({
    segments: types.array(types.frozen()),
    changed: types.optional(types.boolean, false),
    segment: types.maybeNull(types.frozen()),
    segmentId: types.maybeNull(types.string),
    policy: types.maybeNull(types.frozen()),
    loading: types.optional(types.boolean, false),
    holdCards: types.array(types.frozen()),
    holdCardLoading: types.optional(types.boolean, false),
    recreatingApplication: types.optional(types.boolean, false),
    canAddMoratorium: types.optional(types.boolean, false),
    billingDetails: types.maybeNull(BillingDetails),
    policyEquityStatus: PolicyEquityStatus,
    fees: types.array(Fee),
    writeOffTransactions: types.array(types.frozen()),
    inspection: types.optional(types.frozen()),
    bixConversionSignedTimestamp: types.maybeNull(types.string),
    palomarPolicyDecPage: types.maybeNull(types.string),
    currentTotalPaid: types.maybeNull(types.number),
    pendingAmount: types.maybeNull(types.number),
    currentTab: types.optional(types.enumeration('PolicyTabs', Object.values(PolicyTabs)), PolicyTabs.SETTINGS),
    violationsForRenewal: types.maybeNull(types.array(types.frozen())),
    scrollTo: types.maybeNull(types.string),
    branchPlusOffers: types.maybeNull(types.array(types.frozen())),
    revertPreview: types.maybeNull(types.frozen()),
    revertLoading: types.optional(types.boolean, false),
    activePolicyModal: types.maybeNull(types.enumeration('PolicyModalTypes', Object.values(PolicyModalTypes))),
    downloadingAutoPremiumBreakdown: types.optional(types.boolean, false),
    preview: types.maybeNull(types.frozen()),
    isPreviewExpired: types.maybeNull(types.boolean),
    farmNavigatorOpen: types.optional(types.boolean, false)
  })
  .views((self) => ({
    get status() {
      return getPolicyStatus(self.policy);
    },
    get isCancelledOrInCancellation() {
      return [PolicyStatus.Cancelled, PolicyStatus.InCancellation].includes(self.status);
    }
  }))
  // fetching actions
  .actions((self) => ({
    setCurrentTab(newTab) {
      self.currentTab = newTab;
    },
    setActivePolicyModal(modalType) {
      self.activePolicyModal = modalType;
    },
    closePolicyModal() {
      self.activePolicyModal = null;
    },
    setFarmNavigatorOpen(isOpen) {
      self.farmNavigatorOpen = isOpen;
    },
    fetchHoldCards: flow(function* fetchHoldCards(policyId) {
      self.holdCardLoading = true;
      const { client } = getEnv(self);
      try {
        const {
          data: { error: holdCardsError, holdcards }
        } = yield client.query({
          query: GET_HOLD_CARDS,
          fetchPolicy: 'network-only',
          variables: {
            policyId
          }
        });
        if (holdCardsError) {
          throw new Error(holdCardsError);
        }
        self.holdCards = holdcards;
        self.canAddMoratorium = true;
      } catch (e) {
        self.holdCardLoading = false;
        throw e;
      }
    }),
    addHoldCard: flow(function* addHoldCard(reason, policyId) {
      self.holdCardLoading = true;
      const { client } = getEnv(self);
      try {
        const res = yield client.mutate({
          mutation: ADD_HOLD_CARD,
          fetchPolicy: 'no-cache',
          variables: {
            policyId,
            reason
          }
        });
        if (res.data.addHoldCard.success) {
          const { createdBy, createdDate, deleted, holdCardId } = res.data.addHoldCard;
          const cards = [...self.holdCards, { createdBy, createdDate, deleted, holdCardId, reason, policyId }];
          self.holdCards = cards;
          self.holdCardLoading = false;
        }
      } catch (e) {
        self.holdCardLoading = false;
        throw e;
      }
    }),
    removeHoldCard: flow(function* removeHoldCard(policyId, holdCardId) {
      try {
        self.holdCardLoading = true;
        const { client } = getEnv(self);
        yield client.mutate({
          mutation: REMOVE_HOLD_CARD,
          variables: { policyId, holdCardId }
        });
        const newHoldCards = self.holdCards.filter((holdCard) => holdCard.holdCardId !== holdCardId);
        self.holdCards = newHoldCards;
        self.holdCardLoading = undefined;
        return true;
      } catch (e) {
        self.holdCardLoading = undefined;
        throw e;
      }
    }),
    changeSegment: flow(function* changeSegment(
      policyId,
      segment,
      fromRevertModal = false,
      clarionDoorIdOverride = null
    ) {
      const { client } = getEnv(self);
      try {
        if (fromRevertModal) {
          self.revertLoading = true;
        }
        const res = yield client.mutate({
          mutation: CHANGE_SEGMENT,
          variables: {
            policyId,
            segmentId: segment.segmentId,
            segment: GQLTypes.PolicyDetailsInput(segment),
            clarionDoorIdOverride,
            fromPreviewId: self.preview?.id
          }
        });
        track('Staff Segment Change', { segment, changeType: 'edit_change', res });
        const preview = res.data.previewPolicyChangeToSegment;
        if (fromRevertModal) {
          self.revertPreview = preview;
          self.revertLoading = false;
        }

        self.setPreview(preview);
        return {
          ok: true,
          preview
        };
      } catch (err) {
        if (fromRevertModal) {
          self.revertLoading = false;
        }
        return {
          ok: false,
          error: {
            ...err,
            networkError:
              err.networkError ||
              (err.graphQLErrors && err.graphQLErrors.find((e) => e.errorType === 'ExecutionTimeout'))
          }
        };
      }
    }),
    confirm: flow(function* confirm(policyId, previewId, internalNotes) {
      const { client } = getEnv(self);
      try {
        const res = yield client.mutate({
          mutation: CONFIRM,
          variables: {
            policyId,
            previewId,
            internalNotes
          }
        });
        if (!res.data.confirmPolicyChange.success) {
          return {
            ok: false,
            error: {
              message: ''
            }
          };
        }
        track('Staff Policy Change', { notes: internalNotes, res });

        // Clear preview after policy is confirmed
        self.setPreview(null);
        return {
          ok: true,
          ...res.data.confirmPolicyChange
        };
      } catch (error) {
        return {
          ok: false,
          error
        };
      }
    }),
    submitFarmPolicyForUnderwritingApproval: flow(
      function* submitFarmPolicyForUnderwritingApproval(policyId, previewId, policyVersion, answers, internalNote) {
        const { client } = getEnv(self);
        try {
          const res = yield client.mutate({
            mutation: SUBMIT_FARM_POLICY_FOR_UNDERWRITING_APPROVAL,
            variables: {
              previewId,
              policyId,
              policyVersion,
              answers: JSON.stringify(answers),
              internalNote
            }
          });
          if (!res.data.submitUWReviewForApproval.success) {
            return {
              ok: false,
              error: {
                message: 'Failed to submit farm policy for underwriting approval'
              }
            };
          }

          return {
            ok: true,
            ...res.data.submitUWReviewForApproval
          };
        } catch (error) {
          return {
            ok: false,
            error
          };
        }
      }
    ),
    submitChangesForApproval: flow(function* submitChangesForApproval(policyId, policyPreviewLink, internalNote) {
      const { client } = getEnv(self);
      try {
        const res = yield client.mutate({
          mutation: SUBMIT_CHANGES_FOR_APPROVAL,
          variables: {
            policyId,
            policyPreviewLink,
            policyChangesDescription: internalNote,
            customerName: self.primaryNamedInsured
          }
        });
        if (!res.data.sendPolicyChangesForApprovalCommunication.success) {
          return {
            ok: false,
            error: {
              message: 'Failed to submit changes for approval'
            }
          };
        }

        return {
          ok: true,
          ...res.data.sendPolicyChangesForApprovalCommunication
        };
      } catch (error) {
        return {
          ok: false,
          error
        };
      }
    }),
    sendApprovedRequestedChangesCommunication: flow(
      function* sendApprovedRequestedChangesCommunication(policyId, agentUsername) {
        const { client } = getEnv(self);
        try {
          const res = yield client.mutate({
            mutation: SEND_APPROVED_REQUESTED_CHANGES_COMMUNICATION,
            variables: {
              policyId,
              agentUsername
            }
          });
          if (!res.data.sendApprovedRequestedChangesCommunication.success) {
            return {
              ok: false,
              error: {
                message: 'Failed to send approved requested changes communication'
              }
            };
          }

          return {
            ok: true,
            ...res.data.sendApprovedRequestedChangesCommunication
          };
        } catch (error) {
          return {
            ok: false,
            error
          };
        }
      }
    ),
    addSegment: flow(function* addSegment(policyId, segment) {
      const { client } = getEnv(self);
      try {
        const res = yield client.mutate({
          mutation: ADD_SEGMENT,
          variables: {
            policyId,
            segment: GQLTypes.PolicyDetailsInput(segment)
          }
        });
        track('Staff Segment Change', { segment, changeType: 'new_change', res });
        const preview = res.data.previewPolicyChangeAddSegment;

        self.setPreview(preview);
        return {
          ok: true,
          preview
        };
      } catch (err) {
        return {
          ok: false,
          error: {
            ...err,
            networkError:
              err.networkError ||
              (err.graphQLErrors && err.graphQLErrors.find((e) => e.errorType === 'ExecutionTimeout'))
          }
        };
      }
    }),
    changePolicy: flow(function* changePolicy(policy) {
      const { client } = getEnv(self);
      try {
        const res = yield client.mutate({
          mutation: CHANGE_POLICY,
          variables: {
            policy: GQLTypes.PolicyInput(policy)
          }
        });
        const newEndDate =
          res.data.previewPolicyChangePolicy?.diff &&
          JSON.parse(res.data.previewPolicyChangePolicy.diff).full?.addedChanges.endDate;
        track('Staff Preview Policy Change', { res });
        return {
          ok: true,
          preview: {
            ...res.data.previewPolicyChangePolicy,
            // end date can change in policy-mod if in an oden state, so return new endDate and original selected endDate
            endDate: newEndDate || policy.cancellationDate || self.policy.endDate
          },
          reasons: {
            cancelReason: policy.cancelReason,
            nonRenewReason: policy.nonRenewReason
          }
        };
      } catch (error) {
        return {
          ok: false,
          error
        };
      }
    }),
    runManualCharge: flow(
      function* runManualCharge(policyId, accountId, paymentMethod, amount, internalDescription, billType, payAhead) {
        const { client } = getEnv(self);
        return yield client.mutate({
          mutation: RUN_MANUAL_CHARGE,
          fetchPolicy: 'no-cache',
          variables: {
            policyId,
            accountId,
            paymentMethod,
            amount,
            internalDescription,
            billType,
            payAhead
          }
        });
      }
    ),
    addCreditCard: flow(function* addCreditCard({ accountId, policyId, basisTheoryCardToken, customerId, isPrimary }) {
      const { client } = getEnv(self);
      const {
        data: { addCreditCard: res }
      } = yield client.mutate({
        mutation: ADD_CREDIT_CARD,
        variables: {
          policyId,
          accountId,
          basisTheoryCardToken,
          customerId,
          isPrimary
        }
      });
      if (res.success) {
        self.updateCreditCards(res.card);
      }
      return res;
    }),
    changeCreditCard: flow(function* changeCreditCard({ accountId, policyId, basisTheoryCardToken, customerId }) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: MODIFY_CREDIT_CARD,
        variables: {
          policyId,
          accountId,
          basisTheoryCardToken,
          customerId
        }
      });
    }),
    removePaymentMethod: flow(function* removePaymentMethod(paymentMethodId, accountId, policyId) {
      const { client } = getEnv(self);
      const { data } = yield client.mutate({
        mutation: REMOVE_PAYMENT_METHOD,
        variables: {
          paymentMethodId,
          accountId,
          policyId
        }
      });
      self.billingDetails.allPaymentMethods.splice(
        self.billingDetails.allPaymentMethods.findIndex(({ id }) => id === paymentMethodId),
        1
      );
      return data;
    }),
    previewPolicyChangeScrubIncidents: flow(function* previewPolicyChangeScrubIncidents(policy, incidentDates) {
      const { client } = getEnv(self);
      self.loading = true;
      try {
        const res = yield client.mutate({
          mutation: PREVIEW_POLICY_CHANGE_SCRUB_INCIDENTS,
          fetchPolicy: 'no-cache',
          variables: {
            policy,
            incidentDates
          }
        });
        self.loading = false;
        track('Staff Preview Policy Change', { res });
        const preview = res.data.previewPolicyChangeScrubIncidents;
        if (preview?.policy) {
          self.setPolicy(preview.policy);
        }
        return {
          ok: true,
          preview: {
            ...preview,
            endDate: self.policy.endDate
          }
        };
      } catch (e) {
        self.loading = false;
        return {
          ok: false,
          error: e
        };
      }
    }),
    getRenewalPolicyId: flow(function* getRenewalPolicyId(policyId) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.query({
          query: GET_RENEWAL_POLICY_ID,
          variables: { policyId }
        });
        return {
          ok: true,
          renewalPolicyId: data.renewalPolicyId
        };
      } catch (error) {
        return {
          ok: false,
          error
        };
      }
    }),
    getRenewalPriceComparisonDetails: flow(
      function* getRenewalPriceComparisonDetails(currentPolicyId, previousPolicyId) {
        const { client } = getEnv(self);
        try {
          const { data } = yield client.query({
            query: GET_RENEWAL_PRICE_COMPARISON_DETAILS,
            variables: { currentPolicyId, previousPolicyId }
          });
          return {
            ok: true,
            renewalPriceComparisonDetails: data.RenewalPriceComparisonDetails
          };
        } catch (error) {
          return {
            ok: false,
            error
          };
        }
      }
    ),
    setViolationsForRenewal: function setViolationsForRenewal(inputViolations) {
      const parsedViolations = inputViolations.map((violations) => ({
        ...violations,
        autoViolationDates: JSON.parse(violations.autoViolationDates)
      }));

      const violations = [];
      parsedViolations.forEach((record) => {
        const { autoViolationDates } = record;

        Object.keys(autoViolationDates).forEach((date) => {
          const violationsForDate = autoViolationDates[date];

          const driver = self.policy.policyDetails.drivers.find((d) => d.id === record.driverId);

          violationsForDate.forEach((violation) => {
            violations.push({
              date,
              driver: `${driver.firstName} ${driver.lastName}`,
              type: violation.class,
              source: violation.source
            });
          });
        });
      });
      self.violationsForRenewal = violations;
    },
    recreateApplication: flow(function* recreateApplication(policyId) {
      const { client } = getEnv(self);
      self.recreatingApplication = true;

      try {
        const { data } = yield client.mutate({
          mutation: RECREATE_APPLICATION,
          variables: {
            policyId
          }
        });

        return data?.pdfUrl;
      } finally {
        self.recreatingApplication = false;
      }
    }),
    orderInspection: flow(function* orderInspection({
      address,
      fname,
      lname,
      policyId,
      phone,
      mailingAddress,
      coverageA,
      effectiveDate
    }) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.mutate({
          mutation: ORDER_INSPECTION,
          variables: {
            address,
            fname,
            lname,
            policyId,
            phone,
            mailingAddress,
            coverageA,
            effectiveDate
          }
        });

        const { inspection } = yield self.getInspectionStatus({ policyId });
        self.inspection = inspection;

        return data;
      } catch (e) {
        self.loading = false;
        throw e;
      }
    }),
    getInspectionStatus: flow(function* getInspectionStatus({ policyId }) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.mutate({
          mutation: GET_INSPECTION_STATUS,
          variables: { policyId }
        });
        return data;
      } catch (e) {
        self.loading = false;
        throw e;
      }
    }),
    getClarionDoorRequestDiff: flow(function* getClarionDoorRequestDiff({ policyId, policyPreviewId }) {
      const { client } = getEnv(self);
      try {
        const {
          data: { requests },
          error
        } = yield client.query({
          query: GET_CD_DIFF,
          variables: { policyId, policyPreviewId }
        });
        return { requests, error };
      } catch (e) {
        console.error(e);
        throw e;
      }
    }),
    getBixConversationSignatureFromTable: flow(function* getBixConversationSignatureFromTable(policyId) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.query({
          query: GET_BIX_CONVERSION_SIGNATURE_FROM_TABLE,
          variables: {
            policyId
          }
        });

        if (data?.getBixConversationSignatureFromTable?.signedTimestampISO8601) {
          self.bixConversionSignedTimestamp = data.getBixConversationSignatureFromTable.signedTimestampISO8601;
        }
      } catch (e) {
        self.loading = false;
        throw e;
      }
    }),
    fetchPolicyPaidAndPending: flow(function* getPolicyPaidAndPendingCash(policyId) {
      const { client } = getEnv(self);
      try {
        const { getPolicyPaidAndPendingCash } = yield client.query({
          query: GET_POLICY_PAID_AND_PENDING,
          fetchPolicy: 'network-only',
          variables: { policyId }
        });
        const { currentTotalPaid, pendingAmount } = getPolicyPaidAndPendingCash;

        self.currentTotalPaid = currentTotalPaid;
        self.pendingAmount = pendingAmount;
      } catch (e) {
        self.loading = false;
        throw e;
      }
    }),
    runPayAheadManualCharge: flow(function* runPayAheadManualCharge(policy, values) {
      try {
        const { paymentMethod, amount, internalDescription, billType, billingHoldUntil, remainingPayments } = values;
        const accountId = policy.id.slice(0, 9);
        // running manual charge first, and if it fails, will error, & then the billing hold will not be set
        const manualChargeRes = yield self.runManualCharge(
          policy.id,
          accountId,
          paymentMethod,
          amount,
          internalDescription,
          billType,
          true
        );

        if (manualChargeRes.data.runManualCharge.success && remainingPayments > 1) {
          try {
            // if manual charge is successful, and then the billing hold is not,
            // charge will still go through, but user will need to manually set the billing hold
            const changePolicyRes = yield self.changePolicy({ id: policy.id, billingHoldUntil });
            yield self.confirm(policy.id, changePolicyRes.preview.id, 'Pay Ahead Billing Hold'); // confirm policy changes
          } catch {
            return {
              ok: false,
              error: 'Payment success, but billing hold failed, please manually set'
            };
          }
        }

        return {
          ok: true,
          chargeSuccess: manualChargeRes.data.runManualCharge.success,
          paymentMethod: manualChargeRes.data.runManualCharge.type,
          last4: manualChargeRes.data.runManualCharge.last4
        };
      } catch (e) {
        return {
          ok: false,
          error: e
        };
      }
    })
  }))
  // state mutation actions
  .actions((self) => ({
    switchSegment(segmentId) {
      self.segment = getMerged(cloneDeep(toJS(self.segments)), segmentId);
      self.segmentId = segmentId;
      self.loading = false;
    },
    setChanged(changed) {
      self.changed = changed;
    },
    setPolicy(newPolicy) {
      self.policy = newPolicy;
      const { fees, segments } = newPolicy;
      self.fees = fees;
      const mappedSegments = segments.slice().map((s) => clean(cloneDeep(toJS(s))));
      self.segments = cloneDeep(mappedSegments);
      const currentlySelectedSegmentId = self.segment?.segmentId || mappedSegments[mappedSegments.length - 1].segmentId;
      self.segment = getMerged(cloneDeep(mappedSegments), currentlySelectedSegmentId);
    },
    setPreview(preview) {
      if (preview?.id && self.policyType === 'F') {
        self.preview = preview;
        self.setPolicy(preview.policy);

        const url = new URL(window.location.href);
        url.searchParams.set('previewId', preview.id);
        window.history.replaceState({}, '', url.toString());
      }
    },
    clearPolicyPreview() {
      self.preview = null;
      self.isPreviewExpired = null;
      const url = new URL(window.location.href);
      url.searchParams.delete('previewId');
      window.history.replaceState({}, '', url.toString());
    },
    // TODO: Document options
    // TODO: Hook into formik
    addFee(type, options) {
      const protoFee = generateFee(type, options);
      const appliedDate = new Date().toISOString().split('T')[0];
      let description = protoFee.description;
      if (options.state === 'AL' && type === 'installment') {
        description = 'Installment (Variable, update preview to see final cost)';
      }

      self.fees.push({ ...protoFee, type, description, appliedDate, status: 'added' });
    },
    setHoldCards(cards) {
      self.holdCards = cards;
    },
    updateCreditCards(card) {
      self.billingDetails.allPaymentMethods.push(card);
    },
    replaceCreditCard(oldCard, newCard) {
      const currentIndex = self.billingDetails.allPaymentMethods.findIndex((card) => card.id === oldCard.id);
      self.billingDetails.allPaymentMethods[currentIndex] = newCard;
    },
    addBankAccount(account) {
      self.billingDetails.allPaymentMethods.push(account);
    },
    getPalomarPolicyDoc: flow(function* getPalomarPolicyDoc(policyId, repEmail, policyType) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.query({
          query: GET_PALOMAR_POLICY_DOC,
          variables: {
            palomarPolicyId: policyId,
            repEmail,
            policyType
          }
        });
        if (data?.palomarPolicyDoc?.palomarPolicyDecPage) {
          const downloadLink = document.createElement('a');
          downloadLink.target = '_blank';
          downloadLink.href = `data:application/pdf;base64,${data.palomarPolicyDoc.palomarPolicyDecPage}`;
          downloadLink.download = `${policyId}_Dec_Page.pdf`;
          downloadLink.click();

          return { success: true };
        }
        return { error: 'There was an error downloading Palomar Policy document' };
      } catch (error) {
        return { error };
      }
    }),
    downloadAutoPremiumBreakdownPdf: flow(function* downloadAutoPremiumBreakdownPdf(policyId, accountId, segmentId) {
      const { client } = getEnv(self);
      try {
        self.downloadingAutoPremiumBreakdown = true;

        const { data } = yield client.query({
          query: GET_POLICY_AUTO_PREMIUM_BREAKDOWN_PDF,
          variables: {
            policyId,
            accountId,
            segmentId
          },
          fetchPolicy: 'no-cache'
        });

        if (data?.getPolicyAutoPremiumBreakdownPdf) {
          window.open(data.getPolicyAutoPremiumBreakdownPdf, '_blank');
          return { success: true };
        }

        return { error: 'There was an error generating the auto premium breakdown PDF' };
      } catch (error) {
        console.error('Error downloading auto premium breakdown PDF:', error);
        return { error: error.message || 'Failed to download PDF' };
      } finally {
        self.downloadingAutoPremiumBreakdown = false;
      }
    }),
    getAllowLicensedActions(session) {
      /* 
      FormFields have this logic already built in via the permissions prop
      Therefore, only use this function if wanting to disable/enable items that are NOT form fields 
      */
      const state = self.policy?.state;
      return session.allowedStates.includes(state) && !session.viewOnly;
    },
    rewritePolicy: flow(function* rewritePolicy(policyId, newEffectiveDate, internalNotes, newPolicyId) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.mutate({
          mutation: REWRITE_POLICY,
          variables: { policyId, newEffectiveDate, internalNotes, newPolicyId }
        });
        return data.rewritePolicy;
      } catch (e) {
        return e;
      }
    }),
    setScrollTo(scrollTo) {
      self.scrollTo = scrollTo;
    },
    updateBranchPlusOffers(branchPlusOffers) {
      self.branchPlusOffers = branchPlusOffers;
    }
  }))
  .views((self) => ({
    allActiveHoldCards() {
      const cards = self.holdCards
        .slice()
        .filter((card) => !card.deleted)
        .sort((a, b) => (a.createdDate < b.createdDate ? 1 : -1));
      return cards;
    },
    reversedSegments() {
      return self.segments.slice().reverse();
    }
  }))
  .views((self) => ({
    activeHoldCards() {
      // TODO: Add argument for number of cards returned
      return self.allActiveHoldCards().slice(0, 5);
    },
    getActiveHoldCardsByReason(reason) {
      return self.allActiveHoldCards().filter((card) => card.reason === reason);
    },
    get hasBillingHold() {
      return (
        self.policy &&
        self.policy.billingHoldUntil !== null &&
        isBefore(new Date(self.policy.effectiveDate), new Date(self.policy.billingHoldUntil))
      );
    },
    get geographicState() {
      return self.policy?.state;
    },

    get policyType() {
      return self.policy?.policyType;
    },

    get premium() {
      return self.policy?.premium;
    },

    get surplusContribution() {
      return self.policy?.surplusContribution ?? 0.0;
    },

    get totalFeeCost() {
      return self.fees.reduce((total, fee) => total + fee.amount, 0);
    },

    get totalBilled() {
      return self.premium + self.surplusContribution + self.totalFeeCost;
    },

    get writeOffTotal() {
      return self.writeOffTransactions.reduce((total, writeOff) => total + writeOff.amount, 0);
    },

    get totalReceived() {
      const { transactions } = self.billingDetails;

      const paid = transactions?.filter((transaction) =>
        ['credit', 'paid', 'partially refunded', 'pending'].includes(transaction.paymentStatus)
      );

      return paid
        ? paid.reduce((total, transaction) => transaction.paymentAmount - transaction.paymentRefunded + total, 0) +
            self.writeOffTotal
        : 0;
    },

    get allTransactions() {
      const formattedWriteOffTransactions = self.writeOffTransactions.map((transaction) => ({
        id: uuid(),
        paymentDate: transaction.date,
        paymentAmount: transaction.amount,
        paymentRefunded: 0,
        paymentMethod: transaction.paymentMethod,
        paymentStatus: transaction.paymentStatus,
        internalDescription: transaction.description
      }));

      const transactions = self.billingDetails?.transactions || [];

      return [...transactions, ...formattedWriteOffTransactions];
    },

    get earnedPremiumDays() {
      const { policyDays } = self.policyEquityStatus;

      const daysInForce = differenceInDays(new Date(), new Date(self.policy.effectiveDate));

      // if in the future, return 0
      if (daysInForce <= 0) {
        return 0;
      }

      return daysInForce > policyDays ? policyDays : daysInForce;
    },

    get billedPremiumDays() {
      return Math.round(self.policyEquityStatus.premiumBilled / self.policyEquityStatus.blendedDailyRate);
    },

    get paidPremiumDays() {
      return Math.round(self.policyEquityStatus.remainingAfterFees / self.policyEquityStatus.blendedDailyRate);
    },

    get paidEquityDaysRemaining() {
      const endDate = new Date(self.policy.endDate);
      const effectiveDate = new Date(self.policy.effectiveDate);

      // if in the future, 0
      if (isAfter(effectiveDate, new Date())) {
        return 0;
      }

      return isAfter(new Date(), endDate) ? 0 : self.paidPremiumDays - self.earnedPremiumDays;
    },

    get remainingPayments() {
      return self.policyEquityStatus.remainingCostBreakdown.total === 0 ? 0 : self.billingDetails.remainingPayments;
    },

    get nextPaymentAmount() {
      return self.remainingPayments > 0 ? self.billingDetails.nextPaymentAmount - self.writeOffTotal : 0;
    },

    get nextPaymentLabel() {
      const { nextPaymentAmount: amount } = self.billingDetails;

      return amount < 0 ? 'Refund Owed' : 'Next Payment Due';
    },

    get nextPaymentValue() {
      const { nextPaymentDate: date, nextPaymentAmount: amount } = self.billingDetails;

      const value =
        amount <= 0
          ? `${currencyFormatter(Math.abs(amount))}`
          : `${currencyFormatter(amount)} on ${awsDateToDateFormatter(date)}`;
      return value;
    },

    get payAheadNextPayments() {
      return toJS(self.billingDetails.nextPayments).splice(1);
    },

    get isOverdue() {
      return isPolicyOverdue(null, toJS(self?.billingDetails?.transactions));
    },

    get policyId() {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!self.loading && self.policy) {
            resolve(self.policy.id);
          } else {
            setTimeout(checkLoading, 300);
          }
        };

        checkLoading();
      });
    },
    get isBix() {
      return self.policy?.isBix;
    },
    get isAdvancedConnectedHome() {
      return self.isBix
        ? advancedConnectedHomeStates[self.geographicState]?.isBix
        : advancedConnectedHomeStates[self.geographicState]?.isNotBix;
    },
    get showLeaseLoan() {
      return self.isBix
        ? leaseLoanStates[self.geographicState]?.isBix
        : leaseLoanStates[self.geographicState]?.isNotBix;
    },
    get policyDetails() {
      if (!self.policy?.segments) {
        return null;
      }

      const lastIndex = self.policy.segments.length - 1;
      const lastSegmentId = self.policy.segments[lastIndex].segmentId;
      return getMerged(cloneDeep(toJS(self.policy.segments)), lastSegmentId);
    },

    get primaryNamedInsured() {
      const policiesStore = getParent(self);
      const accountStore = getParent(policiesStore);

      if (accountStore.fname && accountStore.lname) {
        return `${accountStore.fname.trim()} ${accountStore.lname.trim()}`;
      }
      return undefined;
    },

    get isFarmOffer() {
      return ['F', 'FA'].includes(self.policyType);
    }
  }));
